import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Send, Code2, CalendarDays, Compass, Wrench, ScanLine, Warehouse, LayoutGrid, FileText, Code,
  QrCode, Mail, Wand2, Video, Home, Globe, Loader2, Settings as SettingsIcon,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// One compact tile per module/tool. `id` is the same feature flag the Feature Flags page edits,
// so both pages always agree; this page only presents the on/off switches in a smaller form.
const SECTIONS = [
  {
    title: 'Modules',
    note: 'Whole parts of the app. OFF hides everything about the module.',
    items: [
      { id: 'transfer_enabled', name: 'File Transfer', icon: Send, text: 'Send page, download links, embed, My Transfers', settings: '/dev/settings/transfer' },
      { id: 'transfer_embed_enabled', name: 'Transfer Embed Widget', icon: Code2, text: 'Copy-paste upload box for client sites (needs File Transfer)', settings: '/dev/settings/transfer' },
      { id: 'events_enabled', name: 'Event Center', icon: CalendarDays, text: 'Events, venues, ticketing, attendees' },
      { id: 'activity_marketplace_enabled', name: 'Tours & Charters', icon: Compass, text: 'Activity directory and charter sellers' },
      { id: 'service_crm_enabled', name: 'Service CRM', icon: Wrench, text: 'Service requests pipeline and funnel page' },
      { id: 'service_repair_enabled', name: 'Service Repair Scanner', icon: ScanLine, text: 'Mechanic scan-in/out and loaner tracking' },
      { id: 'inventory_enabled', name: 'Inventory', icon: Warehouse, text: 'Inventory management in the admin menu' },
      { id: 'showcase_enabled', name: 'Showcase Directory', icon: LayoutGrid, text: 'Portfolio row on home and /apps' },
      { id: 'quotes_enabled', name: 'Quotes', icon: FileText, text: 'Quote system everywhere' },
      { id: 'external_api_enabled', name: 'External API Delivery', icon: Code, text: 'External stack API delivery features' },
    ],
  },
  {
    title: 'Customer dashboard tools',
    note: 'Tools inside the customer dashboard menu (/account). Built, but off until you need them.',
    items: [
      { id: 'portal_qr_codes_enabled', name: 'QR Codes', icon: QrCode, text: 'QR code builder' },
      { id: 'portal_signatures_enabled', name: 'Email Signatures', icon: Mail, text: 'Signature builder and team seats' },
      { id: 'portal_background_remover_enabled', name: 'Background Remover', icon: Wand2, text: 'Image background remover' },
      { id: 'portal_meetings_enabled', name: 'Meetings', icon: Video, text: 'Link to the meetings site' },
    ],
  },
  {
    title: 'File Transfer options',
    note: 'Only apply while File Transfer is ON.',
    requires: 'transfer_enabled',
    items: [
      { id: 'transfer_home_enabled', name: 'Transfer as home page', icon: Home, text: 'The landing page becomes "/"' },
      { id: 'transfer_site_enabled', name: 'Transfer Site template', icon: Globe, text: 'Whole site uses the transfer menu, footer and pages' },
    ],
  },
];

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const DevModules = () => {
  const [flags, setFlags] = useState(null);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/admin-settings/feature-flags`, authHeaders())
      .then((r) => setFlags(r.data || {}))
      .catch(() => toast({ title: 'Error', description: 'Could not load modules', variant: 'destructive' }));
  }, []);

  // The save endpoint takes the whole flag set, so send everything with the one change applied.
  const toggle = async (id) => {
    const next = !flags[id];
    setBusy(id);
    setFlags({ ...flags, [id]: next });
    try {
      await axios.put(`${API}/api/admin-settings/feature-flags`, { ...flags, [id]: next }, authHeaders());
      toast({ title: next ? 'Turned on' : 'Turned off', description: id.replace(/_enabled$/, '').replace(/_/g, ' ') });
    } catch {
      setFlags((f) => ({ ...f, [id]: !next }));
      toast({ title: 'Error', description: 'Could not save that change', variant: 'destructive' });
    } finally {
      setBusy('');
    }
  };

  if (!flags) {
    return <div className="flex justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="w-full" data-testid="dev-modules">
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-gray-900">Modules &amp; Tools</h1>
        <p className="text-sm text-gray-500">Switch parts of the app on or off. These are the same switches as Feature Flags, in a smaller view.</p>
      </div>

      {SECTIONS.filter((s) => !s.requires || flags[s.requires]).map((section) => {
        const on = section.items.filter((i) => flags[i.id]).length;
        return (
          <Card key={section.title} className="mb-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{section.title}</CardTitle>
              <CardDescription>{section.note} {on} of {section.items.length} on.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {section.items.map((item) => {
                  const enabled = Boolean(flags[item.id]);
                  return (
                    <div
                      key={item.id}
                      className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${enabled ? 'bg-white border-emerald-200' : 'bg-gray-50 border-gray-200'}`}
                      data-testid={`module-tile-${item.id}`}
                    >
                      <div className={`mt-0.5 rounded-md p-2 ${enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>
                        <item.icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                        <p className="text-xs text-gray-500 leading-snug">{item.text}</p>
                        {item.settings && enabled && (
                          <Link to={item.settings} className="mt-1 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                            <SettingsIcon className="w-3 h-3" /> Settings
                          </Link>
                        )}
                      </div>
                      <Switch
                        checked={enabled}
                        disabled={busy === item.id}
                        onCheckedChange={() => toggle(item.id)}
                        aria-label={`${item.name} ${enabled ? 'on' : 'off'}`}
                        data-testid={`module-switch-${item.id}`}
                      />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default DevModules;
